import { filter } from 'rxjs/operators';

import { DATE_PIPE_DEFAULT_TIMEZONE, ViewportScroller } from '@angular/common';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ActivatedRoute, Event, Router, Scroll, TitleStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as assetService from '@brafton/assets-api';
import * as atlasApi from '@brafton/atlas-api-client';
import * as coreApiService from '@brafton/core-api-client';
import * as delService from '@brafton/deliverables-client';
import * as logentries from '@brafton/logentries-client';
import {
  BF_AUTH_CONFIG,
  BF_AUTH_CONFIG_OPTIONS,
  BfAuthGuardModule,
  BfAuthServiceModule,
} from '@brafton/skynet-angular-security-service';
import * as textApi from '@brafton/text-api-client';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import {
  AppAtlasConfig,
  ASSETS_API_CLIENT_APP_CONFIG,
  ATLAS_AUTH_CONFIG,
  ATLAS_AUTH_CONFIG_OPTIONS,
  ATLAS_CLIENT_APP_CONFIG,
  CORE_API_CLIENT_APP_CONFIG,
  DELIVERABLES_CLIENT_APP_CONFIG,
  LOGENTRIES_CLIENT_APP_CONFIG,
  TEXTAPI_CLIENT_APP_CONFIG,
} from './core';
import { CoreModule } from './core/core.module';
import { PageTitleStrategy } from './core/services/title-strategy.service';
import { SearchModule } from './search/search.module';
import { SharedModule } from './shared/shared.module';

export const configFactory = (config: AppAtlasConfig) => {
  const configFunction = () => config.load();
  return configFunction;
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    AuthModule,
    BrowserModule,
    CoreModule,
    SharedModule,
    SearchModule,
    BrowserAnimationsModule,

    // Asset Api Modules
    assetService.AssetsAuthInterceptorModule,
    assetService.AssetsModule.forRoot(),

    // Text Api Modules
    textApi.TextServiceModule.forRoot(),

    // Core Api Modules
    coreApiService.CoreApiAuthInterceptorModule,
    coreApiService.AccountsServiceModule.forRoot(),
    coreApiService.ArticleServiceModule.forRoot(),
    coreApiService.CategoriesServiceModule.forRoot(),
    coreApiService.ClientsProfileServiceModule.forRoot(),
    coreApiService.ContractualAllocationServiceModule.forRoot(),
    coreApiService.EditorialArticleServiceModule.forRoot(),
    coreApiService.FeedContentDeliveryServiceModule.forRoot(),
    coreApiService.FeedsServiceModule.forRoot(),
    coreApiService.ProfileAvailabilitiesServiceModule.forRoot(),
    coreApiService.ProfilesServiceModule.forRoot(),
    coreApiService.VideoApiCredentialServiceModule.forRoot(),

    // Deliverables Api Modules
    delService.DeliverablesAuthInterceptorModule,
    delService.AccountManagerServiceModule.forRoot(),
    delService.AccountsServiceModule.forRoot(),
    delService.CompletedProjectsModule.forRoot(),
    delService.ConsultingDeliverableServiceModule.forRoot(),
    delService.ContractServiceModule.forRoot(),
    delService.DeliverableMessageServiceModule.forRoot(),
    delService.DeliverableServiceModule.forRoot(),
    delService.DesignDeliverableServiceModule.forRoot(),
    delService.EditorialDeliverableServiceModule.forRoot(),
    delService.ProductClassesServiceModule.forRoot(),
    delService.ProductDefinitionInstanceServiceModule.forRoot(),
    delService.ProductDefinitionServiceModule.forRoot(),
    delService.ProductsServiceModule.forRoot(),
    delService.ProfilesServiceModule.forRoot(),
    delService.ProjectManagerServiceModule.forRoot(),
    delService.SocialDeliverableServiceModule.forRoot(),
    delService.TechDeliverableServiceModule.forRoot(),
    delService.ToDoServiceModule.forRoot(),
    delService.VideoDeliverableServiceModule.forRoot(),
    delService.WorkTypeServiceModule.forRoot(),

    // Security
    BfAuthGuardModule,
    BfAuthServiceModule.forRoot(),

    //Super Important FallbackRouteModule Needs to be the last App module Loaded
    // ?\FallbackRouteModule,

    atlasApi.AtlasServiceModule.forRoot(),

    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production || environment.staging,
    }),
    //RouterModule.forRoot([], { scrollPositionRestoration: 'enabled' }),
    MatMomentDateModule,
  ],
  providers: [
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    { provide: APP_INITIALIZER, useFactory: configFactory, deps: [AppAtlasConfig], multi: true },
    { provide: assetService.API_CONFIG, useClass: ASSETS_API_CLIENT_APP_CONFIG },
    { provide: BF_AUTH_CONFIG_OPTIONS, useClass: ATLAS_AUTH_CONFIG_OPTIONS },
    { provide: BF_AUTH_CONFIG, useClass: ATLAS_AUTH_CONFIG },
    { provide: coreApiService.CORE_API_CLIENT_CONFIG, useClass: CORE_API_CLIENT_APP_CONFIG },
    { provide: delService.DELIVERABLE_CLIENT_CONFIG, useClass: DELIVERABLES_CLIENT_APP_CONFIG },
    { provide: logentries.LOGENTRIES_CLIENT_CONFIG, useClass: LOGENTRIES_CLIENT_APP_CONFIG },
    { provide: atlasApi.ATLAS_CLIENT_CONFIG, useClass: ATLAS_CLIENT_APP_CONFIG },
    { provide: textApi.TEXTAPI_CLIENT_CONFIG, useClass: TEXTAPI_CLIENT_APP_CONFIG },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    //{ provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: 'UTC' } }, // use this in angular 15 insstead of the following line
    { provide: DATE_PIPE_DEFAULT_TIMEZONE, useValue: 'UTC' },
    AppAtlasConfig,
    ATLAS_AUTH_CONFIG,
    delService.ProductService,
    logentries.LogentriesService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private router: Router,
    private viewportScroller: ViewportScroller,
    private activatedRoute: ActivatedRoute,
  ) {
    const scrollToTopRoutes = [
      // loading production project head
      /\/produce\/productions\/[a-z0-9]{24}$/,
      // loading idea project head
      /\/plan\/ideas\/[a-z0-9]{24}$/,
      // loading campaign project head
      /\/plan\/campaigns\/[a-z0-9]{24}$/,
      // loading production project head
      /\/master\/projects\/[a-z0-9]{24}$/,
      // loading content head
      /\/content\/[a-z0-9]{24}$/,
      // loading profile
      /\/profile\/[0-9]{1,4}$/,
      // loading team
      /\/team\/[0-9]{1,4}$/,
    ];
    this.router.events.pipe(filter((e: Event): e is Scroll => e instanceof Scroll)).subscribe((e: Scroll) => {
      // backwards
      if (e.position) this.viewportScroller.scrollToPosition(e.position);
      // anchor nav
      else if (e.anchor) this.viewportScroller.scrollToAnchor(e.anchor);
      // new forward nav - if we match one of the above specific routes that we DO want to scroll to top, then do so.
      else if (scrollToTopRoutes.some((r) => r.test(e.routerEvent.url))) {
        //console.log('scorll to top');
        this.viewportScroller.scrollToPosition([0, 0]);
      }
    });
  }
}
